import React, { useMemo, useEffect, useRef, useContext } from 'react';
import mapValues from 'lodash/mapValues';
import formErrors from '../locales/es/formErrors';

import deduplicate from '../util/deduplicate';
import { ERRORS_KEY } from '../util/formValidation';
import dfsSearch from '../util/dfsSearch';
import jsonSchemas from '../util/jsonSchemas';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import UWEFormContext from '../context/UWEFormContext';
import FormErrorsContext from '../context/FormErrorsContext';
import { makeFieldId } from '../util/getPathFromId';
import Icon from 'src/components/Icon';

function ErrorListTemplate({ errorSchema, errors: propErrors }) {
  const UweFormContext = useContext(UWEFormContext);
  const { subForms = {} } = UweFormContext || {};
  const { setRjsfErrors } = useContext(FormErrorsContext) || {};

  const subformSchemas = useMemo(() => {
    if (subForms) {
      return mapValues(subForms, sf => sf.schema);
    }
    return {};
  }, [subForms]);

  const errorDataByField = useMemo(
    () =>
      (propErrors || []).reduce((_, error) => {
        const { property, message } = error;

        if (property) {
          const pname = property?.startsWith('.') ? property.substring(1) : property;
          const prop = _[pname] || (_[pname] = {});
          prop[message] = error;
        }

        return _;
      }, {}),
    [propErrors]
  );

  const errors = useMemo(() => {
    if (!errorSchema) return [];

    const errors = [];
    dfsSearch([['', '', errorSchema]], ([prefix, name, current]) => {
      const fringe = [];
      if (!current) {
        return fringe;
      }
      Object.entries(current).forEach(([key, value]) => {
        if (key === ERRORS_KEY) {
          value = value.filter(x => x !== 'should be equal to constant');

          if (prefix && value.length) {
            deduplicate(value).forEach(v =>
              errors.push({
                fieldId: makeFieldId(prefix),
                path: prefix,
                name,
                data: (errorDataByField[prefix] || {})[v],
                msg: v,
              })
            );
          }
        } else {
          fringe.push([prefix ? `${prefix}.${key}` : key, key, value]);
        }
      });

      return fringe;
    });

    return errors;
  }, [errorSchema, errorDataByField]);

  useEffect(() => {
    setRjsfErrors?.(errors);
  }, [errors]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export function ErrorsDropdown({ errors, schema, uiSchema, isOpen, onToggle }) {
  const UweFormContext = useContext(UWEFormContext);
  const { subForms = {}, navigateToField } = UweFormContext || {};
  const subformSchemas = useMemo(() => mapValues(subForms, sf => sf.schema), [subForms]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className="form-errors-trigger" ref={dropdownRef}>
      <button className="form-errors-btn" onClick={onToggle}>
        <Icon icon="WarningCircle" />
        <span>
          {errors.length} {errors.length === 1 ? t`error` : t`errors`}
        </span>
        <Icon icon={isOpen ? 'CaretDown' : 'CaretUp'} className="form-errors-caret" />
      </button>
      {isOpen && (
        <div className="form-errors-dropdown">
          <div className="form-errors-dropdown-header">
            <span>
              <Icon icon="Warning" />
              <Trans>Validation errors</Trans>
            </span>
            <button className="form-errors-close-btn" onClick={onToggle}>
              <Icon icon="X" weight="regular" />
            </button>
          </div>
          <ul className="form-errors-list">
            {errors.map((error, idx) => (
              <ErrorItem
                key={idx}
                err={error}
                schema={schema}
                uiSchema={uiSchema}
                subformSchemas={subformSchemas}
                navigateToField={navigateToField}
                onNavigate={onToggle}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function translateOneError(error, schema) {
  if (!error?.property) return error;
  const schemaChain = jsonSchemas.collectSchemaChainFor(error.property.substring(1), schema);

  const title =
    schemaChain
      .map(sc => (sc.title || '').trim())
      .filter(x => !!x)
      .pop() || '';

  const obj = {
    ...error,
    title,
    message: formErrors[error?.name](error),
  };

  return formErrors[error?.name] ? obj : error;
}

export function transformErrors(errors, schema) {
  return errors && errors.map(error => translateOneError(error, schema));
}

function ErrorItem({ err, schema, subformSchemas, navigateToField, onNavigate }) {
  const { fieldId, name, data, path, msg: defaultMsg } = err;

  function onClickError(e) {
    e.preventDefault();
    if (navigateToField) navigateToField(fieldId, 'danger');
    if (onNavigate) onNavigate();
  }

  const [title, msg] = useMemo(() => {
    let title = data?.title;
    const msg = defaultMsg;

    if (msg.startsWith(':')) {
      return ['', msg.substring(1)];
    }

    if (!title && path) {
      const schemaChain = jsonSchemas.collectSchemaChainFor(path, schema, {}, subformSchemas);
      const pathTitle =
        schemaChain
          .map(sc => (sc.title || '').replaceAll('*', '').trim())
          .filter(x => !!x)
          .pop() || '';
      if (pathTitle) title = pathTitle;
    }

    return [(title || name).replace(/[:*]/g, ''), msg];
  }, [data?.title, name, schema, path, defaultMsg, subformSchemas]);

  return (
    <li className="error-item">
      <button className="error-item-btn" onClick={onClickError}>
        {title ? <span className="error-item-field">{title}</span> : null}
        <span className="error-item-msg">{msg}</span>
      </button>
    </li>
  );
}

export default ErrorListTemplate;
